// Who the guest page is for, settled before anything is drawn (M3, B347; the follow-up to
// docs/ACTIONS-2026-09-17.md B4). Without the page, so a test can hold it.
//
// The messenger API answers a person by their session, else a guest by the guest cookie, and
// the session wins (routes/messenger.ts, whoIs). /guest is mounted before the sign-in gate, so
// a signed-in member of staff can open it, and until this file existed the page drew whatever
// the API returned under a "Guest" label: their own chats, as if they were a guest. Nothing was
// exposed (it is their own data), but the page said something untrue. The rule now: the guest
// pane is drawn only when the API says the requester is a guest. A signed-in person is told in
// words that they are signed in as themselves and offered Chats; and an invite link they open
// while signed in is NOT posted, because the first use of a link marks the invite accepted
// (app.guest_accept) and the person who accepted it would not be the guest.
//
// The real guest path is as it was: a token is posted once and becomes the cookie, a later
// visit reads the cookie, and no session and no cookie asks for the invite link. The one
// request added to it is the read of /me before a token is posted, whose failure of any kind
// changes nothing: only the answer "a person, by session" turns the page away.
import { ApiError, Unauthenticated } from '../api.js';
import type { Me } from './api.js';

export type GuestPhase =
  /** The guest pane. `accepted` says a token was posted just now, so the page takes it out of the address bar. */
  | { kind: 'ready'; name: string; accepted: boolean }
  /** A member of staff's session answered: no pane, the words below and the link to Chats. */
  | { kind: 'signed-in'; name: string; hadInvite: boolean }
  | { kind: 'refused'; message: string };

/** The two calls the page makes, so a test can stand in for the API. */
export interface GuestApi {
  me: (store: null) => Promise<{ me: Pick<Me, 'kind' | 'displayName'> }>;
  guestAccept: (token: string) => Promise<{ guest: { displayName: string } }>;
}

export const NEEDS_INVITE = 'This page needs the invite link you were sent. Open it again from the message, or ask for a new one.';
export const COULD_NOT_OPEN = 'Cobrain could not open the chat.';
export const CHATS_PATH = '/chats';
export const CHATS_LINK_WORDS = 'Go to your chats';

const refusalWords = (e: unknown): string => (e instanceof Unauthenticated ? NEEDS_INVITE : e instanceof ApiError ? e.message : COULD_NOT_OPEN);

/** What a signed-in person is told, a paragraph per entry. The invite sentence only when they arrived by a link. */
export function signedInWords(name: string, hadInvite: boolean): string[] {
  const words = [`You are signed in as ${name}. This page is for guests, who reach it from the invite link they were sent.`];
  if (hadInvite) words.push('The invite link you opened has not been used: it belongs to the guest it was made for. To see what they will see, open it in a private window.');
  words.push('Your own chats are in Chats.');
  return words;
}

/** The token in the address bar's fragment, or none. */
export function inviteTokenFrom(hash: string): string {
  return hash.startsWith('#') ? hash.slice(1) : '';
}

export async function settleGuest(api: GuestApi, token: string): Promise<GuestPhase> {
  // Who does the API say this browser is? Asked first, and only a person by session is acted
  // on here; a 401, a guest or a failure all fall through to the guest path unchanged.
  let me: Pick<Me, 'kind' | 'displayName'> | null = null;
  let meFailed: unknown = null;
  try { me = (await api.me(null)).me; } catch (e) { meFailed = e; }
  if (me?.kind === 'principal') return { kind: 'signed-in', name: me.displayName, hadInvite: token !== '' };
  try {
    if (token) {
      const r = await api.guestAccept(token);
      return { kind: 'ready', name: r.guest.displayName, accepted: true };
    }
    if (me) return { kind: 'ready', name: me.displayName, accepted: false };
    throw meFailed;
  } catch (e) {
    return { kind: 'refused', message: refusalWords(e) };
  }
}
